import React from "react";
import { X } from "lucide-react";
import { getEmojiImage } from "../utils/emotionColorEmoji";
import StudentEmotionTable from "./StudentEmotionTable";

const getEmotionColor = (emotion) => {
  const colors = {
    sad: "#F8FBFD",
    tired: "#F8FBFD",
    lonely: "#F8FBFD",
    scared: "#FCFAFE",
    angry: "#FCFAFE",
    stressed: "#FCFAFE",
    grateful: "#F8FDFA",
    calm: "#F8FDFA",
    happy: "#F8FDFA",
  };
  return colors[emotion] || "#ffffff";
};
const getTextEmotionColor = (emotion) => {
  if (["sad", "tired", "lonely"].includes(emotion)) return "text-emotion-blue";
  if (["scared", "angry", "stressed"].includes(emotion))
    return "text-emotion-purple";
  if (["grateful", "calm", "happy"].includes(emotion))
    return "text-emotion-green";
  return "text-black";
};

const EmotionPopup = ({
  emotion,
  counts,
  totalEmotions,
  topActions,
  onClose,
  studentEmailToNameMap,
  studentEmotionData,
  globalActions,
}) => {
  const [processedActions, setProcessedActions] = React.useState([]);

  React.useEffect(() => {
    const processActions = async () => {
      if (emotion === "all emotions") {
        const actions = Object.entries(globalActions)
          .sort(([, countA], [, countB]) => countB - countA)
          .slice(0, 3)
          .map(([action, count]) => ({
            action,
            percentage: Math.round((count / totalEmotions) * 100),
          }));
        setProcessedActions(actions);
      }
    };

    processActions();
  }, [emotion, globalActions, totalEmotions]);

  if (!emotion) return null;

  // TODO: Talk to Henry to do this calculation on the backend(Data Pipeline)
  const emotionData = counts.find((e) => e.name === emotion);
  const percentage =
    totalEmotions > 0
      ? Math.round((emotionData?.current / totalEmotions) * 100)
      : 0;

  return (
    <div
      className="rounded-lg p-6"
      style={{ backgroundColor: getEmotionColor(emotion) }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <img src={getEmojiImage(emotion)} alt={emotion} className="h-8 w-8" />
          <h3>
            <span className="text-[16px] text-600 capitalize font-semibold">
              {" "}
              {emotion}
            </span>
            {emotion !== "all emotions" && (
              <span className="ml-2 text-[16px] text-600">({percentage}%)</span>
            )}
          </h3>
        </div>
        <button onClick={onClose}>
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="mb-8">
        <p className="text-[12px] mb-2">
          What's helping <span className="font-bold">all students</span> most
          when{" "}
          <span className={`font-semibold ${getTextEmotionColor(emotion)}`}>
            {emotion}
          </span>
        </p>
        <div className="divide-y divide-gray-100">
          {emotion === "all emotions"
            ? processedActions.map(({ action, percentage }) => (
                <div
                  key={action}
                  className="grid grid-cols-[1fr_auto] gap-2 items-center hover:bg-opacity-80 py-2 2xl:p-3"
                >
                  <div className="text-[16px] text-400">{action}</div>
                  <div className="text-right text-[16px] font-semibold text-600">
                    {percentage}%
                  </div>
                </div>
              ))
            : topActions[emotion]?.allActions
                ?.slice(0, 3)
                .map(([action, count], index) => (
                  <div
                    key={action}
                    className="grid grid-cols-[1fr_auto] gap-2 items-center hover:bg-opacity-80 py-2 2xl:p-3"
                  >
                    <div className="text-[16px] text-400">{action}</div>
                    <div className="text-right text-[16px] font-semibold text-600">
                      {Math.round((count / emotionData?.current) * 100)}%
                    </div>
                  </div>
                ))}
        </div>
      </div>

      <div className="mt-8">
        <p className="text-[12px] mb-2">
          What's helping <span className="font-bold">individual students</span>{" "}
          most when{" "}
          <span className={`font-semibold ${getTextEmotionColor(emotion)}`}>
            {emotion}
          </span>
        </p>
        <StudentEmotionTable
          studentEmotionData={studentEmotionData}
          studentEmailToNameMap={studentEmailToNameMap}
        />
      </div>
    </div>
  );
};

export default EmotionPopup;
